import React from 'react';
import { 
  User, 
  Building2, 
  BookOpen, 
  Puzzle, 
  Users, 
  Brain, 
  Trophy, 
  RefreshCw,
  UserCircle
} from 'lucide-react';

export const categories = [
  {
    id: 'personalInfo',
    name: '个人信息',
    icon: <UserCircle className="w-6 h-6 stroke-[1.5]" />,
    iconActive: <UserCircle className="w-6 h-6 stroke-[1.5] text-white" />
  },
  {
    id: 'background',
    name: '能力背景及身份状态',
    icon: <User className="w-6 h-6 stroke-[1.5]" />,
    iconActive: <User className="w-6 h-6 stroke-[1.5] text-white" />
  },
  {
    id: 'industry',
    name: '行业兴趣',
    icon: <Building2 className="w-6 h-6 stroke-[1.5]" />,
    iconActive: <Building2 className="w-6 h-6 stroke-[1.5] text-white" />
  },
  {
    id: 'learning',
    name: '学习内容兴趣模式',
    icon: <BookOpen className="w-6 h-6 stroke-[1.5]" />,
    iconActive: <BookOpen className="w-6 h-6 stroke-[1.5] text-white" />
  },
  {
    id: 'environment',
    name: '学习环境偏好',
    icon: <Puzzle className="w-6 h-6 stroke-[1.5]" />,
    iconActive: <Puzzle className="w-6 h-6 stroke-[1.5] text-white" />
  },
  {
    id: 'social',
    name: '社交行为模式',
    icon: <Users className="w-6 h-6 stroke-[1.5]" />,
    iconActive: <Users className="w-6 h-6 stroke-[1.5] text-white" />
  },
  {
    id: 'decision',
    name: '决策模式',
    icon: <Brain className="w-6 h-6 stroke-[1.5]" />,
    iconActive: <Brain className="w-6 h-6 stroke-[1.5] text-white" />
  },
  {
    id: 'achievement',
    name: '成就与满足感来源',
    icon: <Trophy className="w-6 h-6 stroke-[1.5]" />,
    iconActive: <Trophy className="w-6 h-6 stroke-[1.5] text-white" />
  },
  {
    id: 'adaptability',
    name: '适应力指标',
    icon: <RefreshCw className="w-6 h-6 stroke-[1.5]" />,
    iconActive: <RefreshCw className="w-6 h-6 stroke-[1.5] text-white" />
  }
];

export const categorySteps = [6, 8, 4, 3, 4, 4, 6, 3, 4];

export const totalSteps = categorySteps.reduce((a, b) => a + b, 0);

/**
 * Maps each form field to its corresponding step/question number
 * This is needed to find which question number corresponds to which form field
 */
export const fieldToQuestionMap = {
    // Personal Information (Category 0) - 6 questions
    fullName: 1,
    wechat: 2,
    email: 3,
    school: 4,
    grade: 5,
    major: 6,

    // Background & Identity (Category 1) - 8 questions
    bestCourses: 7,
    topSkills: 8,
    weakness: 9,
    weaknessExamples: 10,
    englishLevel: 11,
    englishLevelOther: 11, // Same question, conditional field
    englishStoryDifference: 12,
    needVisa: 13,
    visaType: 14,
    visaTypeOther: 14, // Same question, conditional field

    // Industry Interests (Category 2) - 4 questions
    industryInterests: 15,
    industryInterestsOther: 15, // Same question, conditional field
    industryInterestsReason: 16,
    notInterestedIndustries: 17,
    careerCertainty: 18,

    // Learning Content Interest (Category 3) - 3 questions
    interestingTopics: 19,
    flowExperience: 20,
    assignmentPreference: 21,

    // Learning Environment (Category 4) - 4 questions
    collaborationStyle: 22,
    motivation: 23,
    feedbackPreference: 24,
    groupRole: 25,

    // Social Behavior (Category 5) - 4 questions
    conflictResolution: 26,
    conflictResolutionOther: 26, // Same question, conditional field
    networkingStyle: 27,
    deadlineResponses: 28,
    workQualityUnderPressure: 29,

    // Decision-Making (Category 6) - 6 questions
    decisionMakingStyle: 30,
    attentionToDetail: 31,
    personDescription: 32,
    vacationPlanning: 33,
    riskPreference: 34,
    riskType: 35,

    // Achievement & Satisfaction (Category 7) - 3 questions
    achievementExperiences: 36,
    successCriteria: 37,
    successCriteriaOther: 37, // Same question, conditional field
    jobFactors: 38,
    jobFactorsOther: 38, // Same question, conditional field

    // Adaptability (Category 8) - 4 questions
    adaptationTime: 39,
    failureResponse: 40,
    criticismHandling: 41,
    ambiguityComfort: 42
  };
  
  /**
   * Determines if a form field has been filled out
   */
  export const isFieldFilled = (field, value) => {
    if (value === undefined || value === null) return false;
    
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    
    return !!value; // Convert to boolean
  };
  
  /**
   * Finds the first blank question in the form data
   * Returns the step number (1-based) of the first unanswered question
   * If all questions are answered, returns the last question
   */
  export const findFirstBlankQuestionStep = (formData) => {
    // Check primary fields only (not conditional fields)
    const primaryFields = [
      'fullName', 'wechat', 'email', 'school', 'grade', 'major',
      'bestCourses', 'topSkills', 'weakness', 'weaknessExamples', 'englishLevel', 'englishStoryDifference', 'needVisa', 'visaType',
      'industryInterests', 'industryInterestsReason', 'notInterestedIndustries', 'careerCertainty',
      'interestingTopics', 'flowExperience', 'assignmentPreference',
      'collaborationStyle', 'motivation', 'feedbackPreference', 'groupRole',
      'conflictResolution', 'networkingStyle', 'deadlineResponses', 'workQualityUnderPressure',
      'decisionMakingStyle', 'attentionToDetail', 'personDescription', 'vacationPlanning', 'riskPreference', 'riskType',
      'achievementExperiences', 'successCriteria', 'jobFactors',
      'adaptationTime', 'failureResponse', 'criticismHandling', 'ambiguityComfort'
    ];
    
    // Find the first blank field
    for (const field of primaryFields) {
      if (!isFieldFilled(field, formData[field])) {
        return fieldToQuestionMap[field];
      }
    }
    
    // If all questions are answered, return the last question
    return totalSteps;
  };